import {type RunHistoryKeyInfo} from '../../types/run';

/**
 * True when two sampled key sets would generate the same panels: the same key
 * names with the same dominant type. Auto panel generation reads nothing else
 * from the key info, so anything else (counts, step) is ignored on purpose.
 *
 * The O(keys) scan is deliberate: retaining the previous store value avoids
 * rerendering key-set subscribers and retriggering useAutoSyncPanelBank's
 * effect. That effect synchronously rebuilds expected panel specs and diffs
 * and initializes the panel bank, even when the generated panels are unchanged.
 * Comparing key names and types pays less work up front to skip that pipeline
 * on repeated poll/refetch results; it allocates no replacement panel specs.
 *
 * Measurements recorded in PR #53327 put this check at about 2.6 ms for 26k
 * keys, versus about 25 ms for local regeneration and 460-620 ms in xAI RUM.
 * These are workload-specific observations, not a fixed cost or a measurement
 * of React render time alone: https://github.com/wandb/core/pull/53327.
 */
export function sampledHistoryKeysEqual(
  a: RunHistoryKeyInfo,
  b: RunHistoryKeyInfo
): boolean {
  if (a === b) {
    return true;
  }
  if (a.sortedKeys.length !== b.sortedKeys.length) {
    return false;
  }
  for (let i = 0; i < a.sortedKeys.length; i++) {
    const key = a.sortedKeys[i];
    if (key !== b.sortedKeys[i]) {
      return false;
    }
    const typeA = a.keys[key]?.typeCounts?.[0]?.type;
    const typeB = b.keys[key]?.typeCounts?.[0]?.type;
    if (typeA !== typeB) {
      return false;
    }
  }
  return true;
}
